/**
 * Step Downloads
 *
 * Displays download artifacts captured during a workflow step's execution.
 * Shows as a compact badge that expands to show download links.
 *
 * @module FlowDesigner/runs/StepDownloads
 */
import React from 'react';
import { DownloadSimple, Table } from '@phosphor-icons/react';
import { Tooltip, TooltipTrigger } from '@chatsdet/ui';
import { API_URL } from '../../../../config';

export interface StepDownloadArtifact {
  stepId: string;
  nodeId: string;
  filename: string;
  s3Key: string;
  sizeBytes: number;
  mimeType: string;
  timestamp: string;
  source?: string;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function StepDownloads({ artifacts }: { artifacts: StepDownloadArtifact[] }) {
  if (artifacts.length === 0) return null;

  return (
    <div className="ml-4 mt-1 space-y-1">
      {artifacts.map((a) => (
        <a
          key={a.s3Key}
          href={`${API_URL}/downloads/${encodeURIComponent(a.s3Key)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
        >
          {a.source === 'table_extraction' ? (
            <Table size={12} weight="bold" />
          ) : (
            <DownloadSimple size={12} weight="bold" />
          )}
          <span className="truncate max-w-[200px]">{a.filename}</span>
          <span className="text-indigo-500 dark:text-indigo-400 text-[10px]">
            {formatSize(a.sizeBytes)}
          </span>
        </a>
      ))}
    </div>
  );
}

export function StepDownloadsBadge({ count }: { count: number }) {
  if (count === 0) return null;

  return (
    <TooltipTrigger delay={0}>
      <span className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 cursor-default">
        <DownloadSimple size={10} weight="bold" />
        {count} download{count !== 1 ? 's' : ''}
      </span>
      <Tooltip>
        {count} file{count !== 1 ? 's' : ''} downloaded during this step
      </Tooltip>
    </TooltipTrigger>
  );
}
